import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { BOOKING_STATUS } from '@/lib/booking-status';
import type { Product, TripWithBookingInfo, TripParticipantCount } from '@/types';

/**
 * 상품 정보 조회 훅
 * @param productId - 조회할 상품 ID
 */
export function useProductData(productId: string | undefined) {
  const { supabase } = useSupabaseConnection();
  return useQuery<Product>({
    queryKey: ['product', productId],
    queryFn: async () => {
      if (!productId) throw new Error('Product ID is required');
      
      const productIdNum = parseInt(productId);
      if (isNaN(productIdNum)) throw new Error('Invalid product ID');
      
      const { data, error } = await supabase
        .from('products')
        .select(`
          product_id,
          name,
          description,
          price,
          duration,
          main_photo_url,
          created_at,
          product_courses!product_id(course_id)
        `)
        .eq('product_id', productIdNum)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
  });
}

/**
 * 예약 가능한 여행 일정 조회 + 예약 현황 포함 훅
 * @param productId - 상품 ID
 * @param userId - 사용자 ID (기존 예약 확인용)
 */
export function useAvailableTrips(productId: string | undefined, userId?: string) {
  const { supabase } = useSupabaseConnection();
  return useQuery<TripWithBookingInfo[]>({
    queryKey: ['availableTrips', productId, userId],
    queryFn: async () => {
      if (!productId) return [];

      // productId를 숫자로 변환하고 유효성 검사
      const productIdNum = parseInt(productId);
      if (isNaN(productIdNum)) return [];

      // 1. 예약 가능한 trips 조회
      // 내일부터 예약 가능 (오늘은 제외)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      // 다음 달 마지막 날까지만 예약 가능
      const nextMonthEnd = new Date();
      nextMonthEnd.setMonth(nextMonthEnd.getMonth() + 2); // 다음 달 말일
      nextMonthEnd.setDate(0); // 이전 달의 마지막 날 = 다음 달의 마지막 날
      nextMonthEnd.setHours(23, 59, 59, 999);
      
      const { data: tripsData, error: tripsError } = await supabase
        .from('trips')
        .select('*')
        .eq('product_id', productIdNum)
        .in('status', ['open', 'pending', 'confirmed'])
        .gte('departure_date', tomorrow.toISOString())
        .lte('departure_date', nextMonthEnd.toISOString())
        .order('departure_date', { ascending: true });

      if (tripsError) throw tripsError;
      if (!tripsData || tripsData.length === 0) return [];

      const tripIds = tripsData.map(trip => trip.trip_id);

      // 2. 전체 예약 현황 조회 (RLS 우회 공개 함수 사용)
      const { data: bookingCounts, error: bookingError } = await supabase
        .rpc('get_trip_participant_counts', { trip_ids: tripIds }) as { data: TripParticipantCount[] | null, error: any };

      if (bookingError) throw bookingError;

      // 3. 현재 사용자의 기존 예약 조회 (로그인된 경우만)
      let userBookings: number[] = [];
      if (userId) {
        const { data: userBookingData, error: userBookingError } = await supabase
          .from('bookings')
          .select('trip_id')
          .eq('user_id', userId)
          .in('trip_id', tripIds)
          .in('status', [BOOKING_STATUS.CONFIRMED, BOOKING_STATUS.PENDING]);

        if (userBookingError) throw userBookingError;
        if (userBookingData) {
          userBookings = userBookingData.map(booking => booking.trip_id);
        }
      }

      // 4. 데이터 조합
      const tripsWithBookingInfo: TripWithBookingInfo[] = tripsData.map(trip => {
        const participantCount = bookingCounts?.find(count => count.trip_id === trip.trip_id);
        return {
          ...trip,
          totalBooked: participantCount ? Number(participantCount.total_participants) : 0,
          isAlreadyBooked: userBookings.includes(trip.trip_id)
        };
      });

      // 5. 같은 날짜의 trip들을 그룹화하고 예약 데이터를 합산
      const uniqueTrips = tripsWithBookingInfo.reduce((acc, trip) => {
        const dateKey = trip.departure_date.split('T')[0];
        const existing = acc.find(t => t.departure_date.split('T')[0] === dateKey);
        
        if (!existing) {
          acc.push(trip);
        } else {
          // 같은 날짜의 trip이 이미 있는 경우
          const existingIndex = acc.indexOf(existing);
          
          // 예약 데이터를 합산하고 더 최근 trip의 정보 사용
          const combinedTrip = {
            ...(trip.trip_id > existing.trip_id ? trip : existing), // 최신 trip의 정보 사용
            totalBooked: existing.totalBooked + trip.totalBooked, // 예약 수 합산
            isAlreadyBooked: existing.isAlreadyBooked || trip.isAlreadyBooked, // 하나라도 예약되어 있으면 true
          };
          
          acc[existingIndex] = combinedTrip;
        }
        
        return acc;
      }, [] as TripWithBookingInfo[]);

      return uniqueTrips;
    },
    enabled: !!productId,
    staleTime: 2 * 60 * 1000, // 2분간 캐시 유지 (예약 현황이 변할 수 있으므로 짧게)
  });
}


/**
 * 선택된 trip의 예약된 좌석 정보 조회 훅
 * @param tripId - 여행 일정 ID
 */
export function useBookedSeats(tripId: number | undefined) {
  const { supabase } = useSupabaseConnection();
  return useQuery<number[]>({
    queryKey: ['bookedSeats', tripId],
    queryFn: async () => {
      if (!tripId) return [];

      // RLS 우회를 위해 공개 RPC 함수 사용
      const { data: seats, error } = await supabase
        .rpc('get_booked_seats', { p_trip_id: tripId });

      if (error) throw error;

      // RPC 함수는 이미 정수 배열을 반환하므로 그대로 사용
      return seats || [];
    },
    enabled: !!tripId,
    staleTime: 5 * 1000, // 5초로 단축하여 실시간성 강화
    refetchInterval: 10 * 1000, // 10초마다 자동 새로고침
    refetchOnWindowFocus: true, // 창 포커스 시 자동 새로고침
  });
}

/**
 * 예약된 좌석의 성별 정보 조회 훅
 * @param tripId - 여행 일정 ID
 */
export interface BookedSeatWithGender {
  seat: number;
  gender: 'male' | 'female' | null;
  name: string;
}

export function useBookedSeatsWithGender(tripId: number | undefined) {
  const { supabase } = useSupabaseConnection();
  return useQuery<BookedSeatWithGender[]>({
    queryKey: ['bookedSeatsWithGender', tripId],
    queryFn: async () => {
      if (!tripId) return [];

      // RLS 우회를 위해 공개 RPC 함수 사용
      const { data: seats, error } = await supabase
        .rpc('get_booked_seats_with_gender', { p_trip_id: tripId });

      if (error) throw error;

      return seats || [];
    },
    enabled: !!tripId,
    staleTime: 5 * 1000,
    refetchInterval: 10 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * 모든 예약 관련 데이터를 통합하여 제공하는 메인 훅
 * @param productId - 상품 ID
 * @param userId - 사용자 ID
 * @param selectedTripId - 선택된 여행 일정 ID
 */
export function useBookingData(
  productId: string | undefined,
  userId: string | undefined,
  selectedTripId?: number
) {
  const { supabase } = useSupabaseConnection();
  const productQuery = useProductData(productId);
  const availableTripsQuery = useAvailableTrips(productId, userId);
  const bookedSeatsQuery = useBookedSeats(selectedTripId);
  const bookedSeatsWithGenderQuery = useBookedSeatsWithGender(selectedTripId);
  const cleanupExecutedRef = useRef(false);

  // 페이지 로드 시 만료된 예약 정리 실행
  useEffect(() => {
    // 이미 실행했거나 productId가 없으면 실행하지 않음
    if (cleanupExecutedRef.current || !productId) {
      return;
    }

    const cleanupExpiredBookings = async () => {
      try {
        console.log('🧹 Initiating expired bookings cleanup...');
        
        const { data, error } = await supabase.functions.invoke('cleanup-expired-bookings', {
          body: {} // 빈 body 전달
        });

        if (error) {
          console.error('⚠️ Failed to cleanup expired bookings:', error);
          // 에러가 발생해도 페이지는 정상 작동해야 함
          return;
        }

        if (data?.cleaned_count > 0) {
          console.log(`✅ Cleaned ${data.cleaned_count} expired bookings:`, data.cleaned_bookings);
          
          // 정리된 예약이 있으면 데이터 리페치
          await Promise.all([
            availableTripsQuery.refetch(),
            bookedSeatsQuery.refetch()
          ]);
        } else {
          console.log('✅ No expired bookings to clean');
        }
      } catch (err) {
        console.error('⚠️ Error during cleanup:', err);
        // 정리 실패해도 페이지는 계속 작동
      }
    };

    // 한 번만 실행되도록 플래그 설정
    cleanupExecutedRef.current = true;
    cleanupExpiredBookings();
  }, [productId]); // productId가 변경될 때만 재실행

  const isLoading = 
    productQuery.isLoading || 
    availableTripsQuery.isLoading;

  const error = 
    productQuery.error || 
    availableTripsQuery.error || 
    bookedSeatsQuery.error;

  return {
    // 개별 쿼리 결과
    product: productQuery.data,
    availableTrips: availableTripsQuery.data || [],
    bookedSeats: bookedSeatsQuery.data || [],
    bookedSeatsWithGender: bookedSeatsWithGenderQuery.data || [],

    // 로딩 상태 (좌석 정보는 선택적이므로 메인 로딩에서 제외)
    isLoading,

    // 에러 상태
    error: error ? '데이터를 불러오는 중 오류가 발생했습니다.' : null,

    // 개별 쿼리 로딩 상태 (세부 제어용)
    productLoading: productQuery.isLoading,
    tripsLoading: availableTripsQuery.isLoading,
    bookedSeatsLoading: bookedSeatsQuery.isLoading,

    // 리페치 함수들
    refetchProduct: productQuery.refetch,
    refetchTrips: availableTripsQuery.refetch,
    refetchBookedSeats: bookedSeatsQuery.refetch,
    refetchBookedSeatsWithGender: bookedSeatsWithGenderQuery.refetch,
  };
}